<template>
	<TabView @tab-change="onTabChange($event.index)">
		<TabPanel :header="$t('Squeeze.DocumentClasses.FieldAttributes')">
			<div class="p-fluid p-formgrid p-grid p-input-filled">
				<div class="p-field p-col-6">
					<label for="name">{{$t('Squeeze.DocumentClasses.Name')}} *</label>
					<InputText id="name" v-model.trim="v$.name.$model" :class="{'p-invalid':v$.name.$invalid && showErrorMessage}" @change="onChangeInput" required="true" autofocus />
					<small v-if="v$.name.required.$invalid && showErrorMessage && !v$.name.technicalValidation.$invalid" class="p-error">
						{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.DocumentClasses.Name')}) }}
					</small>
					<small v-if="v$.name.technicalValidation.$invalid && showErrorMessage" class="p-error">
						{{ $t('Forms.Val.OnlyAllowAlphaNumericUnderscore', { field: $t('Squeeze.DocumentClasses.Name')}) }}
					</small>
					<small v-else-if="!!value.id">
						{{ $t('Squeeze.Admin.WarnRename') }}
					</small>
				</div>
				<div class="p-field p-col-6">
					<label for="description">{{$t('Squeeze.DocumentClasses.Description')}} *</label>
					<DexInputText id="description" v-model.trim="v$.description.$model" :translatedDescription="value.translatedDescription" :translationKey="value.translationKey" :class="{'p-invalid':v$.description.$invalid && showErrorMessage}" @change="onChangeInput" required="true" />
					<small v-if="(v$.description.$invalid && showErrorMessage)" class="p-error">
						{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.DocumentClasses.Description')}) }}
					</small>
				</div>
				<div class="p-field p-col-6">
					<label for="dataType">{{$t('Squeeze.DocumentClasses.DataType')}} *</label>
					<Dropdown id="dataType" v-model="v$.dataType.$model" :options="dataTypes" :class="{'p-invalid':!v$.dataType.$model && showErrorMessage}" optionValue="id" optionLabel="text" @change="onChangeDataType" />
					<small v-if="(!v$.dataType.$model && showErrorMessage)" class="p-error">
						{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.DocumentClasses.DataType')}) }}
					</small>
				</div>
				<div class="p-field p-col-6">
					<label for="externalName">{{$t('Squeeze.DocumentClasses.ExternalName')}}</label>
					<InputText id="externalName" v-model.trim="value.externalName" required="true" />
				</div>
				<div class="p-field p-col-6">
					<label for="headerLocatorId">{{$t('Squeeze.DocumentClasses.HeaderLocator')}}</label>
					<Dropdown id="headerLocatorId" v-model="value.headerLocatorId" :options="locators" :filter="true" :autoFilterFocus="true" optionValue="id" :optionLabel="getTranslatedDescription" :showClear="true" :editable="false" @show="showDropdownOverlay" />
				</div>
				<div class="p-field p-col-6">
					<label for="valueLocatorId">{{$t('Squeeze.DocumentClasses.ValueLocator')}}</label>
					<Dropdown id="valueLocatorId" v-model="value.valueLocatorId" :options="locators" :filter="true" :autoFilterFocus="true" optionValue="id" :optionLabel="getTranslatedDescription" :showClear="true" :editable="false" @show="showDropdownOverlay" />
				</div>
				<div class="p-field p-col-12">
				</div>
				<div class="p-field p-col-3">
					<label for="mandatory">{{$t('Squeeze.DocumentClasses.Mandatory')}}</label><br/>
					<Checkbox id="mandatory" v-model="value.mandatory" :binary="true" @change="onChangeCheckBox('mandatory')" />
				</div>
				<div class="p-field p-col-3">
					<label for="readonly">{{$t('Squeeze.DocumentClasses.ReadOnly')}}</label><br/>
					<Checkbox id="readonly" v-model="value.readonly" :binary="true" @change="onChangeCheckBox('readonly')" />
				</div>
				<div class="p-field p-col-3">
					<label for="hidden">{{$t('Squeeze.DocumentClasses.Hidden')}}</label><br/>
					<Checkbox id="hidden" v-model="value.hidden" :binary="true" @change="onChangeCheckBox('hidden')" />
				</div>
				<div class="p-field p-col-3">
					<label for="forceValidation">{{$t('Squeeze.DocumentClasses.ForceValidation')}}</label><br/>
					<Checkbox id="forceValidation" v-model="value.forceValidation" :binary="true" @change="onChangeCheckBox('forceValidation')" />
				</div>

				<template v-if="value.dataType === 'Text'">
					<div class="p-field p-col-6">
						<label for="maxlength">{{ $t('Squeeze.DocumentClasses.FieldLength') }}</label>
						<InputNumber id="maxlength" v-model="value.maxlength" />
					</div>
					<div class="p-field p-col-6">
						<label for="truncateType">{{ $t('Squeeze.DocumentClasses.CutContent') }}</label>
						<Dropdown
							id="truncateType"
							v-model="value.truncateType"
							:options="truncateTypes"
							optionValue="id"
							optionLabel="text"
							@show="showDropdownOverlay"
							:disabled="value.maxlength < 1"
						/>
					</div>
				</template>

				<div class="p-field p-col-6">
					<label for="width">{{$t('Squeeze.DocumentClasses.TableColumnWidth')}}</label>
					<InputText id="width" v-model.trim="v$.width.$model" :class="{'p-invalid':v$.width.$invalid && showErrorMessage}" @change="onChangeInput" required="true" />
					<small v-if="v$.width.stringWithNumbersValidation.$invalid && showErrorMessage" class="p-error">
						{{ $t('Forms.Val.OnlyAllowNumeric', { field: $t('Squeeze.DocumentClasses.TableColumnWidth')}) }}
					</small>
				</div>
			</div>
		</TabPanel>
		<TabPanel v-if="store.state.featureSet.configurableFieldAmountFormatting" :disabled="!value.dataType || value.dataType !== 'Amount'" :header="$t('Squeeze.General.Presentation')">
			<AmountSeparatorsForm
				:amountSeparatorData="{thousandSeparator: value.thousandSeparator, decimalSeparator: value.decimalSeparator, decimalPlaces: value.decimalPlaces}"
				@update="onChangeAmountSeparators"
			/>
		</TabPanel>
		<TabPanel :disabled="!value.dataType || value.dataType !== 'Text'" :header="$t('Squeeze.DocumentClasses.DbLookup')">
			<DbLookupForm :dbLookUpEntry="value.lookup" :showErrorMessage="showErrorMessage" @update="onUpdateTableLookup"/>
			<template v-if="value.id && value.lookup.active">
				<div class="p-mt-4">{{ $t('Squeeze.DocumentClasses.FieldFilterInfo') }}</div>
				<LookupColumnFiltersView
					:document-class-id="documentClassId"
					:tab-id="columnEntry.tableId"
					:field-id="columnEntry.id"
					:table-id="columnEntry.lookup.tableId"
					:tableColumns="tableColumns"
				></LookupColumnFiltersView>
			</template>
			<template v-else-if="value.lookup.active">
				{{ t('Squeeze.DocumentClasses.PleaseSaveFieldFirst') }}
			</template>
		</TabPanel>
		<TabPanel v-if="showPromptsTab" :disabled="!value.id" :header="$t('Squeeze.Prompts.Prompts')">
			<PromptListView
				v-if="value.id"
				:contextDocumentClassId="documentClassId"
				:contextFieldName="value.name"
				:contextHasLlmClass="llmExtractionActive"
				:lineItem="true"
			/>
			<template v-else>
				{{ $t('Squeeze.DocumentClasses.PleaseSaveFieldFirst') }}
			</template>
		</TabPanel>
	</TabView>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {computed, defineComponent, nextTick, onMounted, PropType, reactive, watch} from "vue";
import Checkbox from "primevue/checkbox";
import InputText from "primevue/inputtext";
import DexInputText from "@/components/DexInputText.vue";
import InputNumber from '@/components/DexInputNumber.vue';
import Dropdown from "primevue/dropdown";
import {DocumentTableColumn, DocumentLocator, LookupDefinition} from "@dex/squeeze-client-ts";
import {useI18n} from "vue-i18n";
import {useVuelidate} from "@vuelidate/core";
import {required} from "@vuelidate/validators";
import DbLookupForm from "@/apps/administration/components/documentclasses/DbLookupForm.vue";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import {
	stringWithNumbersValidation,
	technicalValidation
} from "@/util/ValidationHelper";
import LookupColumnFiltersView from "@/apps/administration/views/squeeze/documentclasses/LookupColumnFiltersView.vue";
import {showDropdownOverlay} from "@/util/StylesHelper";
import AmountSeparatorsForm, {AmountSeparator} from "@/apps/administration/components/documentclasses/AmountSeparatorsForm.vue";
import PromptListView from "@/apps/administration/views/squeeze/prompts/PromptListView.vue";
import useSqueezeStore from "@/apps/squeeze/store";
import {LLM_EXTRACTION_SETTING, useDocumentClassSettings} from "@/composables/useDocumentClassSettings";

interface DataTypes {
	id: string | undefined;
	text: string;
}

export default defineComponent({
	name: 'TableColumnForm',
	components: {
		AmountSeparatorsForm, Checkbox, InputText, DexInputText, InputNumber, Dropdown, DbLookupForm, TabView, TabPanel, LookupColumnFiltersView, PromptListView,
	},
	props: {
		documentClassId: {
			type: Number,
			default: 0,
		},
		columnEntry: {
			type: Object as PropType<DocumentTableColumn>,
			default: () => ({}),
			required: true,
		},
		locators: {
			type: Array as PropType<DocumentLocator[]>,
			default: () => [],
			required: true,
		},
		showErrorMessage: {
			type: Boolean,
			default: false,
		},
		tableColumns: {
			type: Array as PropType<DocumentTableColumn[]>,
			default: () => [],
		},
	},
	emits: ['update', 'onTabChange'],
	setup(props, {emit}) {
		const {t} = useI18n();
		const store = useSqueezeStore();

		/** Settings of the document class currently open (published by DocumentClassView) */
		const {isDocumentClassSettingActive} = useDocumentClassSettings();

		/** The Prompts tab needs the llmExtraction FeatureFlag and admin permissions */
		const showPromptsTab = computed<boolean>(() => {
			return Boolean(store.state.featureSet.llmExtraction) && Boolean(store.state.isAdminUser);
		});

		/** Is maxLLM enabled for the current document class? Drives the hint above the prompt table */
		const llmExtractionActive = computed<boolean>(() => isDocumentClassSettingActive(LLM_EXTRACTION_SETTING));

		/** Current Object of all input-fields */
		const value = reactive<any>(props.columnEntry);

		/** Determines the required rules for validation */
		const rules = {
			name: { required, technicalValidation },
			description: { required },
			dataType: { required },
			width: { stringWithNumbersValidation },
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value, { $scope: false });

		/** List of Data-Types */
		const dataTypes = computed(() => {
			return [
				{
					id: 'Text',
					text: t('Squeeze.General.Text'),
				},
				{
					id: 'Date',
					text: t('Squeeze.General.Date'),
				},
				{
					id: 'Amount',
					text: t('Squeeze.General.Amount'),
				},
			] as DataTypes[];
		});

		/** List of Truncate-Types */
		const truncateTypes = computed(() => {
			return [
				{
					id: DocumentTableColumn.TruncateTypeEnum.None,
					text: t('Squeeze.DocumentClasses.DoNotCutOff'),
				},
				{
					id: DocumentTableColumn.TruncateTypeEnum.Start,
					text: t('Squeeze.DocumentClasses.CutOffStartOfField'),
				},
				{
					id: DocumentTableColumn.TruncateTypeEnum.End,
					text: t('Squeeze.DocumentClasses.CutOffEndOfField'),
				},
			];
		});

		onMounted(async () => {
			Object.assign(value, props.columnEntry);

			// Emit Validated-Values onMounted. Otherwise there can be validation-errors when editing multiple entries
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		});

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(props.columnEntry, async () => {
			Object.assign(value, props.columnEntry);
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		})

		/** Triggered on change tab */
		const onTabChange = (activeTabIndex: number) => {
			emit("onTabChange", activeTabIndex);
		}

		/** Triggered on change of any field */
		const update = async (isInvalidLookup?: boolean) => {
			// need nextTick, because the dropdown don't check the change of a selected element (is to slow)
			await nextTick();
			if (isInvalidLookup) {
				emit("update", value, true, 1);
			} else{
				emit("update", value, v$.value.$invalid, 0);
			}
		}

		/** Triggered on change of any field lookup */
		const onUpdateTableLookup = async (lookupTable: LookupDefinition, isInvalid: boolean) => {
			// need nextTick, because the dropdown don't check the change of a selected element (is to slow)
			await nextTick();
			Object.assign(value.lookup, lookupTable)
			await update(isInvalid);
		}

		/** Triggered on change of input field */
		const onChangeInput = () => {
			update(false);
		}

		/**
		 * Triggered on change of an amount separator
		 * @param data
		 */
		const onChangeAmountSeparators = (data: AmountSeparator) => {
			value.thousandSeparator = data.thousandSeparator;
			value.decimalSeparator = data.decimalSeparator;
			value.decimalPlaces = data.decimalPlaces;
			update(false);
		}

		/**
		 * Set the lookup to inactive if the Type is not text
		 */
		const onChangeDataType = () => {
			if (value.dataType !== "Text") {
				value.lookup!.active = false;
			}
			// Trigger validation
			update(false);
		}

		/**
		 * Is triggered when a checkbox from mandatory, forceValidation or hidden is clicked
		 * @param fieldName Name of the field
		 */
		const onChangeCheckBox = (fieldName: string) => {
			switch(fieldName) {
			case 'mandatory':
			case 'forceValidation': {
				// If a field is mandatory or "force Validation", it shouldn't be hidden
				if (value.mandatory === true || value.forceValidation === true) {
					value.hidden = false;
				}
				break;
			}
			case 'hidden': {
				// If a field is hidden, forcing the Validation can cause a falsy behavior
				if (value.hidden) {
					value.mandatory = false;
					value.forceValidation = false;
				}
				break;
			}
			}
		}

		return {
			getTranslatedDescription,
			t,
			store,
			showPromptsTab,
			llmExtractionActive,
			dataTypes,
			v$,
			value,
			truncateTypes,
			onTabChange,
			update,
			onUpdateTableLookup,
			onChangeInput,
			onChangeAmountSeparators,
			onChangeDataType,
			onChangeCheckBox,
			showDropdownOverlay,
		}
	},
});
</script>

<style scoped>
/** Otherwise there will be scrollbar at the bottom */
.p-grid {
	margin-left: 0px;
	margin-right: 0px;
}

::v-deep(.p-tabview-panel) {
	overflow: hidden;
}
</style>
